<template>
  <div
    :class="{ 'bg-background': !isCameraScreen }"
    class="set-image-modal"
    ref="setImageModal"
  >
    <!-- カメラ撮影画面 -->
    <template v-if="isCameraScreen">
      <div class="camera">
        <div class="camera-text text-white text-center text-size-14 py-3">
          <template v-if="isAvatar">
            枠内に顔全体を収めてください<br />
            帽子やマスク・イヤホンは外してください<br />
            正面を向いて目線はカメラに向けてください
          </template>
        </div>
        <div class="camera-close">
          <FeatherIcon icon="x" stroke="white" @click="onClickCameraClose" />
        </div>
        <div
          v-if="!isAvatar"
          :class="`camera-frame--${photographingType}`"
          class="camera-frame"
        />
        <canvas
          v-show="isAvatar"
          ref="cameraFrameMask"
          class="camera-frame-mask"
        />
        <div v-if="isAvatar" class="fixed-button px-3 pt-3 pb-4">
          <AppButton
            key="shutter"
            block
            class="d-flex align-items-center justify-content-center"
            :disabled="!isCameraActive"
            @click="takePicture"
            ><FeatherIcon icon="camera" class="mr-2" />撮影する</AppButton
          >
        </div>
        <div v-else class="camera-footer">
          <div class="camera-instruction">
            [{{ photographingTypeLabel }}の全体] が枠内に入るようにしてください
          </div>
          <div class="camera-button-wrapper">
            <AppButton
              key="shutter"
              block
              class="d-flex align-items-center justify-content-center camera-button"
              :disabled="!isCameraActive"
              @click="takePicture"
              ><FeatherIcon icon="camera" class="mr-2" />撮影する</AppButton
            >
          </div>
        </div>
      </div>
    </template>
    <!-- 画像確認画面 -->
    <template v-else-if="isPreviewScreen">
      <div class="preview">
        <Header> {{ actionLabel }}した写真の確認 </Header>
        <div class="main-container px-4 py-4">
          <div v-if="imageDescription" class="image-description">
            {{ imageDescription }}
          </div>
          <div class="preview-image mt-4">
            <BAvatar
              v-if="photographingType === 'avatar'"
              :src="previewImageData"
              variant="mild"
              size="20rem"
            />
            <img v-else :src="previewImageData" />
          </div>
          <div class="mt-3">
            <div class="sequence-list" data-testid="sequence-list">
              <SequenceList :items="optionList" />
            </div>
          </div>
          <div class="action-area">
            <div data-testid="footer-content">
              <div class="footer-inner px-3 py-2">
                <div class="my-3 text-center">
                  <SimpleCheckbox
                    v-model:checked="isImageConfirmed"
                    label="上記の写真で問題ない"
                  />
                </div>
                <div class="d-flex action-button-wrapper">
                  <AppButton
                    block
                    @click="onClickApply"
                    :disabled="!isImageConfirmed"
                    data-testid="apply-button"
                    class="mt-0"
                    :rounded="roundedButton"
                    >OK</AppButton
                  >
                </div>
                <div class="text-center my-4">
                  <a
                    href="#"
                    class="retry-link"
                    @click.prevent="onClickRetry"
                    data-testid="retry-link"
                    >再{{ actionLabel }}</a
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <input
      ref="selectImage"
      type="file"
      name="image-uploader"
      accept="image/jpeg, image/png, image/gif, image/webp"
      class="d-none"
      @change="onChangeUpload(($event.target as HTMLInputElement).files?.[0])"
      @cancel="cancelFileSelect"
    />
    <BlockBack />
  </div>
</template>

<script lang="ts">
import AppButton from "@/components/atoms/button/AppButton.vue";
import SimpleCheckbox from "@/components/atoms/input/SimpleCheckbox.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import SequenceList from "@/components/atoms/other/SequenceList.vue";
import FormCheckboxGroup from "@/components/molecules/form/FormCheckboxGroup.vue";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import Header from "@/components/organisms/common/Header.vue";
import { ModalType, PhotographingType, ScreenType } from "@/types/photo";
import {
  b64CropLikeCordova,
  startCamera,
  stopCamera,
  takePicture
} from "@/utils/cameraPreview";
import {
  isCameraAuthorized,
  openApplicationSettings
} from "@/utils/diagnostic";
import { Component, Prop, Vue } from "vue-facing-decorator";

@Component({
  components: {
    Header,
    AppButton,
    FeatherIcon,
    FormCheckboxGroup,
    BlockBack,
    SequenceList,
    SimpleCheckbox
  }
})
export default class SetImageModal extends Vue {
  // 撮影かファイル選択を判別
  @Prop({
    type: String,
    required: true
  })
  modalType!: ModalType;

  // プレビュー画像のチェックリスト
  @Prop({
    type: Array,
    required: true
  })
  options!: string[];

  // プロフィール写真・身分証・資格証の判別
  @Prop({
    type: String,
    required: true
  })
  photographingType!: PhotographingType;

  // フロントカメラ起動フラグ、falseの場合はバックカメラ起動
  @Prop({
    type: Boolean,
    default: true
  })
  isFrontCamera?: boolean;

  // カメラが起動中か判別するフラグ
  @Prop({
    type: Boolean,
    default: true
  })
  isCameraActive?: boolean;

  // 裏面も撮影・選択できるようにするか
  @Prop({
    type: Boolean,
    default: false
  })
  enabledSelectBackImage!: boolean;

  // ボタンに角丸を適用するかどうか
  @Prop({
    type: Boolean,
    default: false
  })
  roundedButton!: boolean;

  @Prop({
    type: String,
    default: ""
  })
  imageDescription!: string;

  // 表示中の画面
  screen: ScreenType = "ready";

  // 撮影・選択した画像データ
  imageData: string | undefined = undefined;

  // プレビューに表示する画像データ
  previewImageData: string | undefined = undefined;

  // カメラパーミッションの承認フラグ
  isCameraAuthorized = false;

  // 裏面画像を一度でも撮影・選択したか
  alreadySelectedBackImage = false;

  // 裏面画像を撮影・選択中か
  isSelectingBackImage = false;

  // 写真確認チェックボックス（単一値）
  isImageConfirmed = false;

  get isAvatar() {
    return this.photographingType === "avatar";
  }

  get isCameraModal() {
    return this.modalType === "camera";
  }

  get isCameraScreen() {
    return this.screen === "camera";
  }

  get isPreviewScreen() {
    return this.screen === "preview";
  }

  get actionLabel() {
    return this.isCameraModal ? "撮影" : "選択";
  }

  get optionList() {
    return this.options.map((option, i) => {
      return {
        value: `image_check_${i}`,
        text: option
      };
    });
  }

  get photographingTypeLabel() {
    switch (this.photographingType) {
      case "avatar":
        return "プロフィール写真";
      case "id":
        return "身分証";
      case "qualification":
        return "資格証";
      default:
        throw new Error("想定外の入力です");
    }
  }

  get fileInput() {
    return this.$refs.selectImage as HTMLInputElement;
  }

  closeModal() {
    this.$emit("closeModal");
    this.reset();
  }

  reset() {
    this.screen = "ready";
    this.imageData = undefined;
    this.isCameraAuthorized = false;
    this.$emit("update:isCameraActive", false);
  }

  async onClickRetry() {
    this.reset();
    if (this.isCameraModal) {
      await this.startCamera();
    } else {
      this.$emit("closeModal");
      this.startFileSelect();
    }
  }

  onClickApply() {
    this.$emit("input", this.previewImageData);
    this.$emit("closeModal");
    this.reset();
  }

  startFileSelect() {
    this.fileInput.click();
  }

  drawCameraFrameMaskForAvatar() {
    if (!this.isAvatar) return;
    const canvas = this.$refs.cameraFrameMask as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return;
    canvas.width = window.screen.width;
    canvas.height = window.screen.height;

    // 背景を半透明の黒で塗りつぶす
    ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 顔の部分をくり抜くための設定
    ctx.globalCompositeOperation = "destination-out";

    // 顔の部分 (楕円形)
    ctx.beginPath();
    const faceWidth = canvas.width * 0.4; // 横幅の40%
    const faceHeight = canvas.height * 0.3; // 縦幅の30%
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    ctx.ellipse(centerX, centerY, faceWidth, faceHeight, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  async validateExtension(file: Blob) {
    const buffer = await file.arrayBuffer();
    const view = new DataView(buffer);
    const signature = view.getUint32(0, false);
    if (
      signature === 0x89504e47 || // PNG
      signature === 0xffd8ffe0 || // JPEG
      signature === 0xffd8ffe1 ||
      signature === 0xffd8ffe2 ||
      signature === 0x47494638 // GIF
    ) {
      return true;
    }
    // WebP
    if (buffer.byteLength >= 12) {
      const webpSignature1 = view.getUint32(0, false);
      const webpSignature2 = view.getUint32(8, false);
      if (webpSignature1 === 0x52494646 && webpSignature2 === 0x57454250) {
        return true;
      }
    }

    return false;
  }

  async onChangeUpload(file?: Blob) {
    if (!file) return;
    const isValid = await this.validateExtension(file);
    if (!isValid) {
      this.$bvModal.msgBoxOk(
        "JPEG、PNG、GIF、WEBPのいずれかの形式の画像をアップロードしてください",
        {
          centered: true
        }
      );
      this.fileInput.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      // readAsDataURLは常に文字列を返す
      this.imageData = e.target?.result as string | undefined;
      this.fileInput.value = "";
      this.screen = "preview";
      this.onAfterImageSelected();
    };
    if (!file) return;
    reader.readAsDataURL(file);
    this.$emit("changeImage");
  }

  cancelFileSelect() {
    // 裏面を選択→キャンセルのような手順を踏んだ場合、裏面の選択状態をクリアしておく
    // ただし、1度でも裏面画像を選択（プレビューに表示）していた場合は何もしない
  }

  onAfterImageSelected() {
    if (!this.imageData) {
      return;
    }
    // プレビュー用の画像を設定
    this.previewImageData = this.imageData;
    // 画面上部にスクロールする
    if (this.$refs.setImageModal) {
      (this.$refs.setImageModal as HTMLDivElement)?.scrollTo(0, 0);
    }
  }

  async onClickCameraClose() {
    this.closeModal();
    await this.stopCamera();
  }

  async startCamera() {
    try {
      const isAuthorized = await isCameraAuthorized();
      if (isAuthorized === false) {
        this.notifyCameraIsNotAuthorized();
        return;
      }
      await startCamera(this.isFrontCamera);
      this.screen = "camera";
      this.$emit("openModal");
      this.isCameraAuthorized = isAuthorized;
      this.$emit("update:isCameraActive", true);
      // screen によって描画するコンテンツを変更しているため、それを待機する
      // これが無いと drawCameraFrameMaskForAvatar で canvas を掴めなくなる
      await this.$nextTick();
      this.drawCameraFrameMaskForAvatar();
    } catch (e) {
      // 開発環境などブラウザを利用している場合
      await this.$bvModal.msgBoxOk(
        "カメラが利用できません。ファイル選択から写真を選択してください。",
        {
          centered: true
        }
      );
      this.closeModal();
    }
  }

  async takePicture() {
    this.isCameraAuthorized = await isCameraAuthorized();

    // カメラのアクセス許可があるかチェック
    if (!this.isCameraAuthorized) {
      this.notifyCameraIsNotAuthorized();
      return;
    }

    try {
      const imageData = await takePicture(1400, 1400);
      b64CropLikeCordova(
        imageData,
        window.screen.width,
        window.screen.height,
        (croppedImgUrl: string) => {
          this.imageData = croppedImgUrl;
          this.onAfterImageSelected();
        }
      );
      this.screen = "preview";
    } catch (e) {
      this.$bvModal.msgBoxOk(
        "撮影できませんでした。もう一度やり直してください。",
        {
          centered: true
        }
      );
      this.reset();
    } finally {
      await this.stopCamera();
    }
  }

  async stopCamera() {
    await stopCamera();
    this.isCameraAuthorized = false;
    this.$emit("update:isCameraActive", false);
  }

  async notifyCameraIsNotAuthorized() {
    const isOpenApplicationSettings = await this.$bvModal.msgBoxConfirm(
      "カメラが利用できません。設定アプリからカメラのアクセスを許可してください。",
      {
        centered: true,
        okTitle: "設定アプリを開く",
        cancelTitle: "キャンセル"
      }
    );

    if (isOpenApplicationSettings) {
      await openApplicationSettings();
    }
    this.closeModal();
  }
}
</script>

<style lang="scss" scoped>
.set-image-modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 200;
  width: 100%;
  height: 100vh;
  overflow: auto;

  .main-container {
    max-width: 768px;
    margin: 0 auto;
  }
}

.fixed-button {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 100;
  width: 100%;
}

.camera-footer {
  position: fixed;
  right: 0;
  bottom: 20px;
  left: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  padding: 12px 0;
}

.camera-instruction {
  padding: 0 1.5rem 12px;
  font-size: 0.8em;
  color: white;
  text-align: center;
}

.camera-button-wrapper {
  width: calc(100% - 3rem);
  margin: 0 1.5rem;
}

.camera-button {
  background-color: #118ab2;
  border-color: #118ab2;
  border-radius: 9999px;
}

.camera {
  padding-top: env(safe-area-inset-top, 0);
  visibility: visible;

  &-text {
    position: relative;
    z-index: 10;
  }

  &-close {
    position: absolute;
    top: calc(env(safe-area-inset-top, 0px) + 14px);
    right: 16px;
    z-index: 10;
  }

  &-frame {
    position: absolute;
    height: auto;

    &::before {
      display: block;
      width: 100%;
      content: "";
    }

    &--id,
    &--qualification {
      top: calc(env(safe-area-inset-top, 0px) + 45px);
      right: 1.5rem;
      bottom: 130px;
      left: 1.5rem;
      border: $blue solid 4px;
      border-radius: 8px;
      box-shadow: 0 0 0 9999em rgba($black, 0.4);
    }
  }
}

.camera-frame-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.preview {
  &-image {
    text-align: center;

    img {
      max-width: 100%;
      max-height: 400px;
      border: 1px solid $gray-03;
      object-fit: contain;
    }
  }

  &-heading {
    margin-top: 32px;
    font-weight: 500;
  }

  :deep(.checkbox-group-items) {
    display: block;
  }

  :deep(.checkbox-group-items-item) {
    &:not(:first-child) {
      margin-top: 12px;
    }

    svg {
      width: 28px;
      min-width: 28px;
      height: 28px;
    }

    span {
      margin-left: 12px !important;
    }
  }
}

.sequence-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 40px;

  &-bottom-space {
    margin-bottom: calc(200px + env(safe-area-inset-bottom));
  }
}

.action-area {
  margin-top: 24px;
}

.footer-content {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 10;
  width: 100vw;
  background-color: #f9f9f9;
  box-shadow: 0 -1px 0 rgb(0 0 0 / 30%);
}

.footer-inner {
  display: flex;
  flex-direction: column;
  max-width: 768px;
  margin: 0 auto;
}

.action-button-wrapper {
  gap: 0.5rem;
}

.retry-link {
  color: #118ab2;
  text-decoration: none;
}

.image-description {
  display: flex;
  justify-content: center;
  margin-top: 40px;
  font-size: 16px;
  font-weight: 700;
}
</style>
